import React from 'react';
import { motion } from 'framer-motion';
import { X, ArrowUp, ArrowDown } from 'lucide-react';
import colors from '../../config/colors';

/**
 * Compact chip + numbered-ranked-list layout for Q17.
 * - Top:   wrapping pill chips for every option. Clicking adds to the ranked list.
 * - Below: numbered list of selected items with up/down arrows to reorder
 *          and an × button to remove.
 * Previous two-column drag-and-drop layout is replaced.
 */
const NumberedSelectQuestion = ({ question, value, onChange }) => {
  const selected = value || [];
  const maxSelect = question.maxSelect || 5;
  const minSelect = question.minSelect || 1;

  const optionLabel = (v) =>
    question.options.find((o) => o.value === v)?.label || v;

  const toggle = (v) => {
    if (selected.includes(v)) {
      onChange(selected.filter((x) => x !== v));
    } else if (selected.length < maxSelect) {
      onChange([...selected, v]);
    }
  };

  const remove = (v) => onChange(selected.filter((x) => x !== v));

  const move = (idx, direction) => {
    const next = [...selected];
    const target = idx + direction;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  return (
    <div style={{ padding: '8px 0' }}>
      <h2
        style={{
          fontSize: 'clamp(1.4rem, 2.8vw, 1.9rem)',
          fontWeight: 700,
          color: colors.textPrimary,
          marginBottom: 6,
          lineHeight: 1.2,
        }}
      >
        {question.question}
      </h2>
      {question.subtitle && (
        <p style={{ fontSize: '0.95rem', color: colors.textSecondary, marginBottom: 12 }}>
          {question.subtitle}
        </p>
      )}

      <div
        style={{
          fontSize: '0.82rem',
          fontWeight: 600,
          color: colors.brandPrimary,
          marginBottom: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
        }}
      >
        Pick up to {maxSelect} — {selected.length}/{maxSelect} chosen
      </div>

      {/* Chip row */}
      <div
        style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18 }}
        data-testid="q17-chip-row"
      >
        {question.options.map((opt) => {
          const isSel = selected.includes(opt.value);
          const atMax = !isSel && selected.length >= maxSelect;
          return (
            <motion.button
              key={opt.value}
              onClick={() => toggle(opt.value)}
              disabled={atMax}
              whileHover={{ scale: atMax ? 1 : 1.03 }}
              whileTap={{ scale: atMax ? 1 : 0.97 }}
              data-testid={`q17-chip-${opt.value}`}
              style={{
                background: isSel ? colors.brandPrimary : colors.bgCard,
                border: `2px solid ${isSel ? colors.brandPrimary : colors.borderMedium}`,
                borderRadius: 9999,
                padding: '10px 16px',
                fontSize: '0.9rem',
                fontWeight: 600,
                color: isSel ? colors.textInverse : colors.textPrimary,
                cursor: atMax ? 'not-allowed' : 'pointer',
                opacity: atMax ? 0.45 : 1,
                transition: 'all 0.2s ease',
              }}
            >
              {opt.label}
            </motion.button>
          );
        })}
      </div>

      {/* Divider + ranked list */}
      <div
        style={{
          borderTop: `1px solid ${colors.borderMedium}`,
          paddingTop: 16,
        }}
      >
        <div
          style={{
            fontSize: '0.8rem',
            fontWeight: 700,
            color: colors.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: 10,
          }}
        >
          Your ranked priorities
        </div>

        {selected.length === 0 ? (
          <div
            style={{
              padding: '18px 16px',
              border: `2px dashed ${colors.borderMedium}`,
              borderRadius: 12,
              color: colors.textMuted,
              fontSize: '0.9rem',
              textAlign: 'center',
            }}
            data-testid="q17-ranked-empty"
          >
            Tap a chip above to build your top {maxSelect}.
          </div>
        ) : (
          <ol
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
            data-testid="q17-ranked-list"
          >
            {selected.map((v, idx) => (
              <li
                key={v}
                data-testid={`q17-ranked-item-${v}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  background: colors.bgCard,
                  border: `2px solid ${colors.brandPrimary}`,
                  borderRadius: 10,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: colors.brandPrimary,
                    color: colors.textInverse,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    flexShrink: 0,
                  }}
                >
                  {idx + 1}
                </div>
                <div
                  style={{
                    flex: 1,
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    color: colors.textPrimary,
                  }}
                >
                  {optionLabel(v)}
                </div>
                <button
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  aria-label="Move up"
                  data-testid={`q17-move-up-${v}`}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: idx === 0 ? 'not-allowed' : 'pointer',
                    color: idx === 0 ? colors.borderMedium : colors.textSecondary,
                    padding: 4,
                  }}
                >
                  <ArrowUp size={18} />
                </button>
                <button
                  onClick={() => move(idx, 1)}
                  disabled={idx === selected.length - 1}
                  aria-label="Move down"
                  data-testid={`q17-move-down-${v}`}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: idx === selected.length - 1 ? 'not-allowed' : 'pointer',
                    color:
                      idx === selected.length - 1 ? colors.borderMedium : colors.textSecondary,
                    padding: 4,
                  }}
                >
                  <ArrowDown size={18} />
                </button>
                <button
                  onClick={() => remove(v)}
                  aria-label="Remove"
                  data-testid={`q17-remove-${v}`}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: colors.textMuted,
                    padding: 4,
                  }}
                >
                  <X size={18} />
                </button>
              </li>
            ))}
          </ol>
        )}

        {selected.length > 0 && selected.length < minSelect && (
          <div
            style={{
              marginTop: 12,
              padding: '8px 12px',
              background: '#FEF3C7',
              borderRadius: 8,
              fontSize: '0.85rem',
              color: '#92400E',
              textAlign: 'center',
            }}
          >
            Select at least {minSelect} to continue.
          </div>
        )}
      </div>
    </div>
  );
};

export default NumberedSelectQuestion;
